import {
  customInstance,
  getApiAuthMe,
  getErrorMessage,
  NormalizedApiError,
  postApiAuthLogin,
  postApiAuthLogout,
  postApiOauthRequest,
  putApiUserChangePassword,
  type CurrentUserInfo,
  type OAuthRequestResponse,
} from '@smartsash/api-client';
import type { LoginEvent, SessionStream, Unsubscribe } from '@smartsash/realtime';

import { installAuthHttpHooks } from './httpBridge';
import {
  authReducer,
  initialAuthState,
  type AuthError,
  type AuthResult,
  type AuthState,
  type MfaMethod,
  type Session,
} from './machine';
import { selectTokenStore, type AuthTokens, type TokenStore, type TokenStoreMode } from './tokens';
import type {
  AuthApi,
  AuthOperations,
  ChangePasswordInput,
  MfaVerifyInput,
  SignInCredentials,
} from './useAuth';

export type AuthApp = 'admin' | 'portal';
export type AuthStrategy = 'credentials' | 'oauth-redirect';

/**
 * The `loginChannel` each app sends with `POST /api/Auth/login` (HS-1584, backend HS-1574).
 *
 * The backend refuses a login without one (`400 LOGIN_CHANNEL_REQUIRED`) and checks the
 * channel's own permission — `Auth.Login.Admin` or `Auth.Login.Portal` — after the credentials.
 * The values happen to equal `AuthApp`; the map keeps the wire spelling in one named place rather
 * than relying on that coincidence at every call site.
 */
export const LOGIN_CHANNEL = {
  admin: 'admin',
  portal: 'portal',
} as const satisfies Record<AuthApp, string>;

export type LoginChannel = (typeof LOGIN_CHANNEL)[AuthApp];

/**
 * The `errorCode` values `POST /api/Auth/login` distinguishes its refusals with (HS-1584).
 *
 * `missingPermission` arrives as a 403 after the credentials were accepted: the account exists but
 * may not sign in to THIS app. Reporting it as "wrong password" would send the user retyping a
 * password that is correct. Spelled exactly as `AuthFlowService.LoginAsync` emits them.
 */
export const LOGIN_ERROR_CODE = {
  missingPermission: 'MISSING_PERMISSION',
  channelRequired: 'LOGIN_CHANNEL_REQUIRED',
} as const;

export interface AuthRoutes {
  login: string;
  changePassword: string;
  oauthCallback: string;
  /**
   * Where a signed-in session belongs (HS-1408).
   *
   * The guard needs it to send an authenticated session OFF a challenge screen. Without it the
   * guard had nowhere to redirect, so it let every authenticated session through to every route —
   * including `/change-password`, where the form silently did nothing because `changePassword`
   * refuses to run outside the challenge state.
   *
   * Required, not optional: an app that omitted it would quietly reintroduce that bug, and both
   * apps already answer this question the same way (`/dashboard`, their post-login default).
   */
  home: string;
  /** Required only when the backend returns an MFA challenge. */
  mfa?: string;
}

export interface OAuthRedirectRequest {
  identityProvider?: string;
}

export interface OAuthRedirectResponse {
  authorizationUrl: string;
  state?: string;
  identityProvider?: string;
}

export interface CreateAuthConfig {
  app: AuthApp;
  strategies: readonly AuthStrategy[];
  tokenStore?: TokenStore | TokenStoreMode;
  routes: AuthRoutes;
  /** Override only the backend flows whose wire contract differs from the current OpenAPI snapshot. */
  operations?: Partial<AuthOperations>;
  requestOAuthRedirect?: (request: OAuthRedirectRequest) => Promise<OAuthRedirectResponse>;
  navigate?: (authorizationUrl: string) => void;
  /** Install the shared api-client 401 and 403 hooks. Disable only in isolated tests. */
  installHttpHooks?: boolean;
  refresh?: (tokenStore: TokenStore) => Promise<AuthTokens | null>;
  /**
   * Where a 403 from any endpoint is reported (HS-1387). This package owns no logger, so the
   * sink is the app's — admin passes its `shared/logger`. Omit it and a 403 still refreshes the
   * permission snapshot, it is simply not written down anywhere.
   */
  reportForbidden?: (ctx: { status: number; url?: string }) => void;
}

export interface AuthClient extends AuthApi {
  readonly app: AuthApp;
  readonly routes: AuthRoutes;
  readonly strategies: readonly AuthStrategy[];
  readonly tokenStore: TokenStore;
  readonly state: AuthState;
  /** True after the first restore attempt settles. */
  readonly ready: boolean;
  subscribe(listener: () => void): Unsubscribe;
  /**
   * Put the session into `passwordChangeRequired` from outside a sign-in (HS-1225).
   *
   * Two callers need this, and neither can reach the reducer any other way. A hard reload loses
   * the in-memory challenge while the backend's cookie stays valid, so the app re-asserts it from
   * whatever it persisted; and admin's lab-onboarding flows authenticate through their own
   * endpoint, learn `requiresPasswordChange` from that response, and must hand the session to the
   * same change-password screen.
   *
   * Idempotent, and a no-op once the session is authenticated — re-asserting a challenge over a
   * live session would lock a signed-in user out of the app.
   */
  requirePasswordChange(flowToken: string): void;
  /**
   * Re-read `/Auth/me` for an ALREADY authenticated session and fold the result in (HS-1387).
   *
   * `restore()` cannot do this: it short-circuits on `ready`, and the `SESSION_RESTORED` event
   * it dispatches only settles an unauthenticated machine. So before this existed the permission
   * snapshot was written once, at sign-in, and a permission change — including one the signed-in
   * user made themselves — reached the UI only after a sign-out and back in.
   *
   * Single-flight: N callers in one burst share one request. A no-op resolving to `null` outside
   * `authenticated` — there is no snapshot to refresh, and a session that is gone is the 401
   * hook's business, not this method's.
   */
  refreshSession(): Promise<Session | null>;
  startOAuthRedirect(request?: OAuthRedirectRequest): Promise<void>;
  /** Bind the backend LoginEvent emitted after Cognito completes through OAuthController. */
  bindOAuthSession(stream: SessionStream): Unsubscribe;
}

/**
 * The flow token the package's own operations use (HS-1292).
 *
 * The session is carried by the backend cookie, so the token is a label rather than a secret.
 * It is exported because every caller that re-asserts the challenge from outside a sign-in —
 * a restored session, or an app flow that authenticates through its own endpoint — has to use
 * the same one.
 */
export const PASSWORD_CHANGE_FLOW_TOKEN = 'password-change';

/**
 * A backend that predates the HS-1292 fix answers a temporary-password login with
 * `301 Moved Permanently` and the same body. Production still runs it, so the status stays
 * acceptable until it is gone; stage already answers `200`. Both are read the same way.
 */
const STATUS_OK = 200;
const STATUS_LEGACY_PASSWORD_CHANGE_REQUIRED = 301;

/**
 * `skipForbiddenPolicy` (HS-1584): a login answers 403 when the account lacks this app's login
 * permission. There is no session yet, so the shared 403 policy — re-read `/Auth/me`, report the
 * endpoint — has nothing to refresh and would only log a sign-in refusal as an access violation.
 */
const LOGIN_REQUEST_OPTIONS = {
  skipAuthRefresh: true,
  skipForbiddenPolicy: true,
  validateStatus: (status: number) =>
    status === STATUS_OK || status === STATUS_LEGACY_PASSWORD_CHANGE_REQUIRED,
} as const;

const AUTH_ERROR = {
  invalidSession: 'auth.error.invalidSession',
  passwordChangedSignInAgain: 'auth.error.passwordChangedSignInAgain',
  oauthFailed: 'auth.error.oauthFailed',
  oauthUrlMissing: 'auth.error.oauthUrlMissing',
  strategyDisabled: 'auth.error.strategyDisabled',
} as const;

/**
 * The `errorCode` values `PUT /api/User/change-password` distinguishes its 400s with (HS-1405).
 *
 * Both arrive as HTTP 400, so the status alone cannot tell them apart and the screens used to
 * report a refused NEW password as a wrong TEMPORARY one. Verified against stage on 2026-09-02;
 * the strings are the backend's, spelled exactly as `UserController.ChangePassword` emits them.
 *
 * `policyViolation` also covers "old/new password is required", which the screens' own schema
 * refuses before a request is made — so by the time one reaches the UI it can only be the policy.
 */
export const CHANGE_PASSWORD_ERROR_CODE = {
  policyViolation: 'ValidationError',
  wrongCurrentPassword: 'InvalidCurrentPassword',
} as const;

const MFA_ENDPOINTS = {
  verify: '/api/Auth/mfa/verify',
  resend: '/api/Auth/mfa/resend',
} as const;

function failed(messageKey: string, detail?: string): AuthResult {
  const error: AuthError = detail ? { messageKey, detail } : { messageKey };
  return { outcome: 'failed', error };
}

/**
 * Build the `AuthError` for a thrown request, keeping the backend's `errorCode` (HS-1405).
 *
 * `getErrorMessage` maps the STATUS, which is 400 for two unrelated change-password failures.
 * The code is what separates them, and `normalizeError` has already read it off the body — it was
 * simply being dropped here.
 */
export function authErrorFromThrown(error: unknown): AuthError {
  const errorCode = error instanceof NormalizedApiError ? error.errorCode : undefined;
  return {
    messageKey: getErrorMessage(error),
    ...(error instanceof Error && error.message ? { detail: error.message } : {}),
    ...(errorCode ? { errorCode } : {}),
  };
}

/**
 * Does this result take the session out of its flow for good? (HS-1225)
 *
 * A rejected challenge step now returns to its challenge so it can be retried, and the tokens
 * that carry that flow have to survive with it — clearing them on every failure is what used to
 * make a mistyped temporary password unrecoverable in `bearer` mode.
 */
function endsFlow(result: AuthResult): boolean {
  return result.outcome === 'failed' && result.error.endsFlow === true;
}

function normalizeMfaMethod(value: string): MfaMethod | null {
  switch (value.toLowerCase()) {
    case 'totp':
      return 'totp';
    case 'email':
      return 'email';
    case 'sms':
      return 'sms';
    default:
      return null;
  }
}

function readTokens(value: unknown): AuthTokens | null {
  if (typeof value !== 'object' || value === null) return null;
  const record = value as Record<string, unknown>;
  const accessToken = record.accessToken ?? record.access_token;
  const refreshToken = record.refreshToken ?? record.refresh_token;
  if (typeof accessToken !== 'string' || typeof refreshToken !== 'string') return null;
  const expiresAt = record.expiresAt ?? record.expires_at;
  return typeof expiresAt === 'number'
    ? { accessToken, refreshToken, expiresAt }
    : { accessToken, refreshToken };
}

function readMfaChallenge(value: unknown): AuthResult | null {
  if (typeof value !== 'object' || value === null) return null;
  const record = value as Record<string, unknown>;
  const challenge = record.challenge ?? record.outcome;
  const mfaRequired = challenge === 'mfa' || challenge === 'mfaRequired';
  if (!mfaRequired) return null;

  const mfaToken = record.mfaToken ?? record.mfa_token;
  const rawMethods = record.methods ?? record.availableMethods;
  if (typeof mfaToken !== 'string' || !Array.isArray(rawMethods)) return null;
  const methods = rawMethods.flatMap((method) => {
    if (typeof method !== 'string') return [];
    const normalized = normalizeMfaMethod(method);
    return normalized ? [normalized] : [];
  });
  return methods.length > 0 ? { outcome: 'mfaRequired', mfaToken, methods } : null;
}

function toSession(info: CurrentUserInfo): Session | null {
  if (!info.userId) return null;
  return {
    user: {
      userId: info.userId,
      ...(info.institutionId ? { institutionId: info.institutionId } : {}),
      ...(info.institutionName ? { institutionName: info.institutionName } : {}),
      ...(info.accountId ? { accountId: info.accountId } : {}),
      ...(info.email ? { email: info.email } : {}),
      ...(info.login ? { login: info.login } : {}),
      ...(info.firstName ? { firstName: info.firstName } : {}),
      ...(info.lastName ? { lastName: info.lastName } : {}),
    },
    permissions: info.permissions ?? [],
    // Carried only when reported. `undefined` is what makes an empty permission list readable
    // as genuine rather than withheld — see `permissionsReported`.
    ...(info.sessionId ? { sessionId: info.sessionId } : {}),
    // Copied only when the backend actually said something: `undefined` is "not reported", which
    // a caller with its own remembered flag must be able to tell apart from a reported `false`.
    ...(typeof info.requiresPasswordChange === 'boolean'
      ? { requiresPasswordChange: info.requiresPasswordChange }
      : {}),
  };
}

/**
 * `skipForbiddenPolicy` breaks the one loop this endpoint can create (HS-1387): the 403 policy
 * re-reads `/Auth/me`, so a 403 from `/Auth/me` itself would re-read it again, and again.
 * Single-flight does not help — those calls are sequential, not concurrent.
 */
const SESSION_READ_OPTIONS = { skipForbiddenPolicy: true } as const;

async function restoreFromApi(): Promise<Session | null> {
  try {
    return toSession(await getApiAuthMe(SESSION_READ_OPTIONS));
  } catch (error) {
    if (getErrorMessage(error) === 'errors.unauthorized') return null;
    throw error;
  }
}

function createApiOperations(tokenStore: TokenStore, app: AuthApp): AuthOperations {
  return {
    async signIn(credentials) {
      try {
        const response = await postApiAuthLogin(
          { ...credentials, loginChannel: LOGIN_CHANNEL[app] },
          LOGIN_REQUEST_OPTIONS,
        );
        const challenge = readMfaChallenge(response);
        if (challenge) return challenge;
        // HS-1292: the backend sets the session cookies and THEN reports that the password is
        // temporary, so the session is live but must not be used for anything else. Read before
        // the tokens are stored: a challenge is not a sign-in.
        if (response.requiresPasswordChange === true) {
          return { outcome: 'passwordChangeRequired', flowToken: PASSWORD_CHANGE_FLOW_TOKEN };
        }
        const tokens = readTokens(response);
        if (tokens) tokenStore.setTokens(tokens);
        const session = await restoreFromApi();
        return session ? { outcome: 'authenticated', session } : failed(AUTH_ERROR.invalidSession);
      } catch (error) {
        // HS-1584: keep the backend's `errorCode` — a 403 `MISSING_PERMISSION` is the only thing
        // that separates "this account may not sign in here" from any other refusal.
        return { outcome: 'failed', error: authErrorFromThrown(error) };
      }
    },
    async verifyMfa(input: MfaVerifyInput & { mfaToken: string }) {
      try {
        const response = await customInstance<unknown>(
          {
            url: MFA_ENDPOINTS.verify,
            method: 'POST',
            data: input,
          },
          { skipAuthRefresh: true },
        );
        const tokens = readTokens(response);
        if (tokens) tokenStore.setTokens(tokens);
        const session = await restoreFromApi();
        return session ? { outcome: 'authenticated', session } : failed(AUTH_ERROR.invalidSession);
      } catch (error) {
        return failed(getErrorMessage(error), error instanceof Error ? error.message : undefined);
      }
    },
    async resendMfa(mfaToken: string) {
      await customInstance(
        {
          url: MFA_ENDPOINTS.resend,
          method: 'POST',
          data: { mfaToken },
        },
        { skipAuthRefresh: true },
      );
    },
    /**
     * HS-1292: the real change, no longer a stub.
     *
     * The wire contract is recorded — `PUT /api/User/change-password` takes
     * `{ oldPassword, newPassword }`, field for field the package's `ChangePasswordInput` — and
     * the request rides the session the sign-in already established, which the backend keeps
     * usable for exactly this call while `RequiresPasswordChange` is true.
     *
     * `flowToken` is unused: the flow is carried by the session itself, not by a token. It is in
     * the signature because the client passes it.
     *
     * Never rejects. An escaping rejection strands the machine mid-transition, because the
     * resolving `PASSWORD_CHANGED` event is dispatched from the awaited result.
     */
    async changePassword({
      oldPassword,
      newPassword,
    }: ChangePasswordInput & { flowToken: string }): Promise<AuthResult> {
      try {
        await putApiUserChangePassword({ oldPassword, newPassword });
        const session = await restoreFromApi();
        if (session) return { outcome: 'authenticated', session };
        // The password WAS changed and the session is gone — the backend rotated or dropped the
        // cookie on the change. Offering a retry would ask for a temporary password that no
        // longer exists, so the flow ends and the user signs in with what they just chose.
        return {
          outcome: 'failed',
          error: { messageKey: AUTH_ERROR.passwordChangedSignInAgain, endsFlow: true },
        };
      } catch (error) {
        // HS-1405: the code, not just the status — a 400 here is either a refused new password or
        // a wrong temporary one, and the screen has to say which.
        return { outcome: 'failed', error: authErrorFromThrown(error) };
      }
    },
    async signOut() {
      await postApiAuthLogout({ skipAuthRefresh: true });
    },
    restore: restoreFromApi,
  };
}

async function requestOAuthFromApi(request: OAuthRedirectRequest): Promise<OAuthRedirectResponse> {
  const response: OAuthRequestResponse = await postApiOauthRequest(
    { identityProvider: request.identityProvider },
    { skipAuthRefresh: true },
  );
  if (!response.authorizationUrl) throw new Error(AUTH_ERROR.oauthUrlMissing);
  return {
    authorizationUrl: response.authorizationUrl,
    ...(response.state ? { state: response.state } : {}),
    ...(response.identityProvider ? { identityProvider: response.identityProvider } : {}),
  };
}

function navigateBrowser(authorizationUrl: string): void {
  if (typeof window === 'undefined') throw new Error('OAuth redirect requires a browser navigator');
  window.location.assign(authorizationUrl);
}

async function refreshFromApi(tokenStore: TokenStore): Promise<AuthTokens | null> {
  const persisted = tokenStore.loadPersisted();
  const response = await customInstance<unknown>(
    {
      url: '/api/Auth/refresh',
      method: 'POST',
      ...(tokenStore.mode === 'bearer' && persisted
        ? { data: { refreshToken: persisted.refreshToken } }
        : {}),
    },
    { skipAuthRefresh: true },
  );
  const tokens = readTokens(response);
  if (tokenStore.mode === 'bearer' && !tokens) {
    throw new Error('Bearer refresh response did not contain tokens');
  }
  return tokens;
}

export function createAuth(config: CreateAuthConfig): AuthClient {
  const strategies = [...new Set(config.strategies)];
  if (strategies.length === 0) throw new Error('createAuth requires at least one strategy');

  const tokenStore = selectTokenStore(config.tokenStore);
  const defaults = createApiOperations(tokenStore, config.app);
  const operations: AuthOperations = { ...defaults, ...config.operations };
  const listeners = new Set<() => void>();
  let state: AuthState = initialAuthState;
  let ready = false;
  let restoring: Promise<void> | null = null;
  let refreshing: Promise<Session | null> | null = null;

  const notify = () => {
    for (const listener of listeners) listener();
  };
  const dispatch = (event: Parameters<typeof authReducer>[1]) => {
    const next = authReducer(state, event);
    if (next === state) return;
    state = next;
    notify();
  };
  const markReady = () => {
    if (ready) return;
    ready = true;
    notify();
  };
  const resetReady = () => {
    if (!ready) return;
    ready = false;
    notify();
  };
  const ensureStrategy = (strategy: AuthStrategy) => {
    if (!strategies.includes(strategy))
      throw new Error(`${AUTH_ERROR.strategyDisabled}: ${strategy}`);
  };
  const resolveSession = (): Promise<Session | null> => operations.restore();

  const client: AuthClient = {
    app: config.app,
    routes: config.routes,
    strategies,
    tokenStore,
    get state() {
      return state;
    },
    get ready() {
      return ready;
    },
    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    async signIn(credentials: SignInCredentials) {
      ensureStrategy('credentials');
      dispatch({ type: 'SIGN_IN' });
      const result = await operations.signIn(credentials);
      if (result.outcome === 'failed') tokenStore.setTokens(null);
      dispatch({ type: 'SIGN_IN', result });
      markReady();
    },
    async verifyMfa(input: MfaVerifyInput) {
      const current = state;
      if (current.status !== 'mfaRequired') return;
      dispatch({ type: 'MFA_VERIFY' });
      const result = await operations.verifyMfa({ ...input, mfaToken: current.mfaToken });
      if (endsFlow(result)) tokenStore.setTokens(null);
      dispatch({
        type: 'MFA_VERIFY',
        result,
      });
    },
    async resendMfa() {
      if (state.status !== 'mfaRequired') return;
      const { mfaToken } = state;
      dispatch({ type: 'MFA_RESEND' });
      await operations.resendMfa(mfaToken);
    },
    async changePassword(input: ChangePasswordInput) {
      const current = state;
      if (current.status !== 'passwordChangeRequired') return;
      dispatch({ type: 'PASSWORD_CHANGED' });
      const result = await operations.changePassword({ ...input, flowToken: current.flowToken });
      if (endsFlow(result)) tokenStore.setTokens(null);
      dispatch({
        type: 'PASSWORD_CHANGED',
        result,
      });
    },
    async signOut() {
      await operations.signOut();
      tokenStore.setTokens(null);
      dispatch({ type: 'SIGN_OUT' });
      resetReady();
    },
    restore() {
      if (ready) return Promise.resolve();
      restoring ??= resolveSession()
        .then((session) => {
          if (!session) return;
          // HS-1292: a session on a temporary password is live but unusable. `SESSION_RESTORED`
          // would settle it as `authenticated` and let the app in, so the challenge is entered
          // instead — that is what a reload, or a second tab, has to land on.
          if (session.requiresPasswordChange === true) {
            client.requirePasswordChange(PASSWORD_CHANGE_FLOW_TOKEN);
            return;
          }
          if (state.status === 'authenticating') {
            dispatch({ type: 'SIGN_IN', result: { outcome: 'authenticated', session } });
          } else {
            dispatch({ type: 'SESSION_RESTORED', session });
          }
        })
        .finally(() => {
          restoring = null;
          markReady();
        });
      return restoring;
    },
    refreshSession() {
      if (state.status !== 'authenticated') return Promise.resolve(null);
      refreshing ??= resolveSession()
        .then((session) => {
          // Re-read AFTER the await: a sign-out, an expiry or a challenge may have landed while
          // the request was in flight, and none of them wants this answer folded in.
          if (state.status !== 'authenticated') return null;
          // `null` means the operations layer could not produce a session. It is deliberately
          // NOT treated as a sign-out: admin's `restore` maps every failure to `null`, so a
          // transient 500 is indistinguishable from a dead session here, and dropping a live
          // session over one failed background read would be worse than the staleness this
          // method exists to fix. A genuinely dead session is the 401 hook's job — the request
          // above already went through it.
          if (!session) return null;
          // The server says this session is back on a temporary password — an administrator
          // reset it while the user was signed in. Folding that in as a normal update would
          // leave them inside the app holding a password they must change, so the challenge
          // takes precedence over the snapshot. `SIGN_IN` is dispatched rather than
          // `requirePasswordChange()`, which deliberately refuses to act on a live session.
          // The package's own flow token is used even by an app that mints its own: the token
          // is a label, no screen reads it, and the flow is carried by the session itself.
          //
          // This branch trusts the server completely, and that is a real dependency: a backend
          // that keeps reporting `true` after accepting a change would pull the user back out
          // of the app on every window focus, not once. `/Auth/me` reads the flag off the
          // account row it just updated, so there is no cache to lag behind — but if that ever
          // stops being true, this is the line that turns it into a loop.
          if (session.requiresPasswordChange === true) {
            dispatch({
              type: 'SIGN_IN',
              result: { outcome: 'passwordChangeRequired', flowToken: PASSWORD_CHANGE_FLOW_TOKEN },
            });
            return session;
          }
          dispatch({ type: 'SESSION_UPDATED', session });
          return session;
        })
        .finally(() => {
          refreshing = null;
        });
      return refreshing;
    },
    sessionExpired() {
      const shouldSurfaceExpiry = ready || state.status === 'authenticated';
      tokenStore.setTokens(null);
      if (shouldSurfaceExpiry) {
        dispatch({ type: 'SESSION_EXPIRED' });
        markReady();
      }
    },
    requirePasswordChange(flowToken: string) {
      if (state.status === 'authenticated' || state.status === 'passwordChangeRequired') return;
      dispatch({ type: 'SIGN_IN', result: { outcome: 'passwordChangeRequired', flowToken } });
      markReady();
    },
    async startOAuthRedirect(request = {}) {
      ensureStrategy('oauth-redirect');
      dispatch({ type: 'SIGN_IN' });
      try {
        const response = await (config.requestOAuthRedirect ?? requestOAuthFromApi)(request);
        (config.navigate ?? navigateBrowser)(response.authorizationUrl);
      } catch (error) {
        dispatch({
          type: 'SIGN_IN',
          result: failed(
            getErrorMessage(error) === 'errors.unknown'
              ? AUTH_ERROR.oauthFailed
              : getErrorMessage(error),
            error instanceof Error ? error.message : undefined,
          ),
        });
        markReady();
        throw error;
      }
    },
    bindOAuthSession(stream) {
      ensureStrategy('oauth-redirect');
      let settled = false;
      dispatch({ type: 'SIGN_IN' });
      return stream.on('LoginEvent', (event: LoginEvent) => {
        if (settled) return;
        settled = true;
        void (async () => {
          if (!event.success) {
            tokenStore.setTokens(null);
            dispatch({ type: 'SIGN_IN', result: failed(AUTH_ERROR.oauthFailed) });
            markReady();
            return;
          }
          if (event.accessToken && event.refreshToken) {
            tokenStore.setTokens({
              accessToken: event.accessToken,
              refreshToken: event.refreshToken,
            });
          }
          try {
            const session = await resolveSession();
            if (!session) tokenStore.setTokens(null);
            dispatch({
              type: 'SIGN_IN',
              result: session
                ? { outcome: 'authenticated', session }
                : failed(AUTH_ERROR.invalidSession),
            });
          } catch (error) {
            tokenStore.setTokens(null);
            dispatch({
              type: 'SIGN_IN',
              result: failed(
                getErrorMessage(error),
                error instanceof Error ? error.message : undefined,
              ),
            });
          } finally {
            markReady();
          }
        })();
      });
    },
  };

  if (config.installHttpHooks ?? true) {
    installAuthHttpHooks({
      tokenStore,
      refresh: () => (config.refresh ?? refreshFromApi)(tokenStore),
      onSessionExpired: () => {
        client.sessionExpired();
      },
      refreshSession: () => client.refreshSession(),
      ...(config.reportForbidden ? { reportForbidden: config.reportForbidden } : {}),
    });
  }

  return client;
}
